/**
 * Quality validation type definitions for GenFlappy
 *
 * Defines interfaces and constants for the asset quality validation pipeline
 * that ensures grayscale compliance, dimension accuracy, and edge transparency.
 *
 * @module types/validation
 */

import type { CachedAsset } from './cache';

/**
 * Extract AssetType from CachedAsset (reuse existing type definition)
 * Prevents duplicate type definitions and TypeScript errors
 */
export type AssetType = CachedAsset['assetType'];

/**
 * Result of quality validation check for a single asset
 *
 * @interface ValidationResult
 * @property {number} overallScore - Weighted average score (0-100)
 * @property {boolean} passed - True if overallScore >= 80
 * @property {number} grayscaleScore - Grayscale deviation score (0-100)
 * @property {number} dimensionScore - Dimension accuracy score (0-100)
 * @property {number} transparencyScore - Edge transparency score (0-100)
 * @property {string[]} failureReasons - Array of specific validation failures
 * @property {number} validationDurationMs - Time taken for validation
 */
export interface ValidationResult {
  /** Weighted average score (0-100) */
  overallScore: number;

  /** True if overallScore >= 80 */
  passed: boolean;

  /** Grayscale deviation score (0-100) */
  grayscaleScore: number;

  /** Dimension accuracy score (0-100) */
  dimensionScore: number;

  /** Edge transparency score (0-100) */
  transparencyScore: number;

  /** Array of specific validation failures */
  failureReasons: string[];

  /** Time taken for validation in milliseconds */
  validationDurationMs: number;
}

/**
 * Target dimensions for each asset type
 *
 * @interface DimensionTarget
 * @property {number} width - Expected width in pixels
 * @property {number} height - Expected height in pixels
 */
export interface DimensionTarget {
  /** Expected width in pixels */
  width: number;

  /** Expected height in pixels */
  height: number;
}

/**
 * Validation threshold constants
 *
 * These thresholds define the quality requirements for assets:
 * - Grayscale: Max 15% RGB deviation from pure grayscale
 * - Dimension: ±5% tolerance for width and height
 * - Transparency: Min 80% of edge pixels must be transparent
 * - Overall: Min 80 score to pass validation
 */
export const VALIDATION_THRESHOLDS = {
  /** Maximum allowed grayscale deviation percentage (15%) */
  GRAYSCALE_DEVIATION_MAX: 15,

  /** Dimension tolerance percentage (±5%) */
  DIMENSION_TOLERANCE: 5,

  /** Minimum edge transparency percentage (80%) */
  TRANSPARENCY_MIN: 80,

  /** Overall passing score threshold (80 out of 100) */
  OVERALL_PASSING_SCORE: 80,
} as const;

/**
 * Target dimensions for each asset type
 *
 * Character: 128x128px (player sprite)
 * Obstacle: 256x512px (pipes)
 * Background: 720x1280px (scrolling background)
 */
export const TARGET_DIMENSIONS: Record<AssetType, DimensionTarget> = {
  character: { width: 128, height: 128 },
  obstacle: { width: 256, height: 512 },
  background: { width: 720, height: 1280 },
} as const;
